import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import {
  useQpmBuildingAddressListQuery,
  useQpmBuildingDeleteMutation,
  useQpmBuildingListQuery,
} from "@/services/backend/qpm/buildings/api/building-api";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import {
  Building,
  BuildingList,
} from "@/services/backend/qpm/buildings/types/building";
import {
  Address,
  newAddress,
} from "@/services/backend/addresses/types/address";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";

export type UseManageBuildingDataResult = {
  processId?: string;
  processCustomerId?: string;
  hasAddressList: boolean;

  isLoading: boolean;
  processError: unknown;
  addressError: unknown;
  buildingError: unknown;

  postalAddress: Address;
  serviceObjectIds: string[];

  buildingList: BuildingList | undefined;
  selectedBuilding: Building | undefined;

  onBuildingSelect: (buildingId: string) => void;
  onBuildingCreated: (building: Building) => void;
  onDeleteBuilding: (buildingId: string) => void;
};

export function useManageBuildingData(
  selectedServiceObjectId?: string,
): UseManageBuildingDataResult {
  const { toast } = useToast();
  const { processId } = useParams();

  const [selectedBuilding, setSelectedBuilding] = useState<Building>();
  const [currentBuildingId, setCurrentBuildingId] = useState<string | null>();

  const {
    data: process,
    error: processDataError,
    isLoading: processDataIsLoading,
  } = useProcessesProcessShowQuery({
    id: processId ?? "",
  });

  const { data: addressList, error: addressListError } =
    useQpmBuildingAddressListQuery(
      {
        customerId: process?.customerId as string,
      },
      {
        skip: !process?.customerId,
      },
    );

  const addressV2 = addressList?.addresses?.[0];

  const postalAddress = useMemo(
    () => addressV2?.postalAddress ?? newAddress(),
    [addressV2?.postalAddress],
  );

  const serviceObjectIds = useMemo(
    () => addressV2?.serviceObjectIds ?? [],
    [addressV2?.serviceObjectIds],
  );

  const {
    data: buildingList,
    error: errorBuildingList,
    isLoading: buildingIsLoading,
  } = useQpmBuildingListQuery(
    {
      serviceObject: { active: true, values: [selectedServiceObjectId!] },
    },
    {
      skip: !selectedServiceObjectId,
    },
  );

  const [
    deleteBuilding,
    {
      error: errorDeleteBuilding,
      reset: resetDeleteBuilding,
      isSuccess: isSuccessDeleteBuilding,
    },
  ] = useQpmBuildingDeleteMutation();

  useEffect(() => {
    if (buildingList) {
      const building = buildingList.buildings.find(
        (b) => b.id === currentBuildingId,
      );
      if (building) {
        setSelectedBuilding(building);
        setCurrentBuildingId(building.id);
      } else {
        setSelectedBuilding(buildingList.buildings[0]);
      }
    }
  }, [buildingList, currentBuildingId]);

  useEffect(() => {
    if (
      !selectedBuilding &&
      buildingList &&
      buildingList.buildings.length > 0
    ) {
      setSelectedBuilding(buildingList.buildings[0]);
      setCurrentBuildingId(buildingList.buildings[0].id);
    }
  }, [buildingList, selectedBuilding]);

  useEffect(() => {
    if (errorDeleteBuilding) {
      toast({ ...parseRTKQueryError(errorDeleteBuilding) });
      resetDeleteBuilding();
    }
  }, [errorDeleteBuilding, resetDeleteBuilding, toast]);

  useEffect(() => {
    if (isSuccessDeleteBuilding) {
      if (!buildingList || buildingList.buildings.length <= 0) {
        setSelectedBuilding(undefined);
        setCurrentBuildingId(undefined);
      } else {
        setSelectedBuilding(buildingList.buildings[0]);
        setCurrentBuildingId(buildingList.buildings[0].id);
      }
    }
  }, [isSuccessDeleteBuilding, buildingList]);

  const onBuildingSelect = useCallback(
    (buildingId: string) => {
      if (!buildingList) return;
      const building = buildingList.buildings.find((b) => b.id === buildingId);
      if (building) {
        setSelectedBuilding(building);
        setCurrentBuildingId(building.id);
      }
    },
    [buildingList],
  );

  const onBuildingCreated = useCallback(
    (building: Building) => {
      if (selectedBuilding && selectedBuilding.id === building.id) {
        return;
      }
      setSelectedBuilding(building);
      setCurrentBuildingId(building.id);
    },
    [selectedBuilding],
  );

  const onDeleteBuilding = useCallback(
    (buildingId: string) => {
      deleteBuilding({ id: buildingId });
    },
    [deleteBuilding],
  );

  return {
    processId,
    processCustomerId: process?.customerId,
    hasAddressList: !!addressList?.addresses,
    isLoading: processDataIsLoading || buildingIsLoading,
    processError: processDataError,
    addressError: addressListError,
    buildingError: errorBuildingList,
    postalAddress,
    serviceObjectIds,
    buildingList,
    selectedBuilding,
    onBuildingSelect,
    onBuildingCreated,
    onDeleteBuilding,
  };
}
